'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { OpenStatusBadge } from './OpenStatusBadge'
import type { OpenStatus } from '@/lib/hours/openStatus'

type Props = {
  businessName: string
  tagline?: string
  heroImageUrl?: string
  heroVideoUrl?: string
  city?: string | null
  pickupWaitMinutes?: number
  readyInLabel?: string
  onViewMenu: () => void
  onDelivery?: () => void
  hasDelivery?: boolean
  openStatus?: OpenStatus | null
}

/**
 * Hero del tema "classic" (Moderno limpio): foto a sangre con tratamiento
 * editorial-moderno — kicker con líneas, nombre en sans fuerte, chips glass y
 * CTAs sólidos sin adornos (sin emojis). Composición centrada y aireada,
 * distinta del vibrant (anclado abajo-izquierda). Sin foto, cae a un fondo de
 * acento con CTA invertido. Lee tokens del tema (--ms-*).
 */
export function HeroClassic({
  businessName,
  tagline,
  heroImageUrl,
  heroVideoUrl,
  city,
  pickupWaitMinutes,
  readyInLabel,
  onViewMenu,
  onDelivery,
  hasDelivery,
  openStatus,
}: Props) {
  const t = useTranslations('public.microsite')
  const hasMedia = !!(heroImageUrl || heroVideoUrl)

  return (
    <section
      className="relative w-full overflow-hidden"
      style={{
        minHeight: 'min(640px, 84vh)',
        background: hasMedia
          ? '#0a0a0a'
          : 'linear-gradient(150deg, var(--ms-accent) 0%, color-mix(in srgb, var(--ms-accent) 65%, #000) 100%)',
      }}
    >
      {heroVideoUrl ? (
        <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" src={heroVideoUrl} />
      ) : heroImageUrl ? (
        <Image src={heroImageUrl} alt={businessName} fill priority sizes="100vw" className="object-cover" />
      ) : null}

      {/* Scrim balanceado: deja respirar la foto y mantiene el texto legible
          (overlay vertical suave + realce radial detrás del título centrado). */}
      {hasMedia && (
        <>
          <div className="absolute inset-0 z-10" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.46) 0%, rgba(0,0,0,0.28) 42%, rgba(0,0,0,0.58) 100%)' }} />
          <div className="absolute inset-0 z-10" style={{ background: 'radial-gradient(68% 54% at 50% 45%, rgba(0,0,0,0.4), transparent 76%)' }} />
        </>
      )}

      {/* Contenido centrado */}
      <div
        className="relative z-20 mx-auto flex max-w-3xl flex-col items-center justify-center px-6 text-center"
        style={{ minHeight: 'min(640px, 84vh)' }}
      >
        {city && (
          <div className="flex items-center gap-3 mb-5">
            <span className="block h-px w-7 sm:w-10" style={{ background: 'rgba(255,255,255,0.38)' }} />
            <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.26em] text-white/90 pl-[0.26em]">
              <span className="w-[6px] h-[6px] rounded-full" style={{ background: 'var(--ms-accent)' }} />
              {city}
            </span>
            <span className="block h-px w-7 sm:w-10" style={{ background: 'rgba(255,255,255,0.38)' }} />
          </div>
        )}

        <h1
          className="text-white font-black tracking-[-0.02em] leading-[0.98] text-[clamp(44px,8vw,82px)]"
          style={{ fontFamily: 'var(--ms-font-display)', textShadow: '0 4px 30px rgba(0,0,0,0.4)' }}
        >
          {businessName}
        </h1>

        {tagline && <p className="mt-4 max-w-xl text-[17px] sm:text-[19px] text-white/85">{tagline}</p>}

        {/* Chips glass */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          <OpenStatusBadge status={openStatus ?? null} />
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/12 backdrop-blur-sm border border-white/20 text-white px-3.5 py-1.5 text-[13px] font-medium">
            {t('pickupAvailable')}
          </span>
          {pickupWaitMinutes ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/12 backdrop-blur-sm border border-white/20 text-white px-3.5 py-1.5 text-[13px] font-medium">
              {readyInLabel ?? t('readyIn', { minutes: pickupWaitMinutes })}
            </span>
          ) : null}
        </div>

        {/* CTAs */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={onViewMenu}
            className="inline-flex items-center rounded-full px-8 py-3.5 text-[15px] font-bold transition hover:scale-[1.03] active:scale-95"
            style={
              hasMedia
                ? { background: 'var(--ms-accent)', color: '#fff', boxShadow: '0 14px 34px -12px color-mix(in srgb, var(--ms-accent) 70%, transparent)' }
                : { background: '#fff', color: 'var(--ms-accent)' }
            }
          >
            {t('orderOnline')}
          </button>
          {hasDelivery && onDelivery && (
            <button
              type="button"
              onClick={onDelivery}
              className="inline-flex items-center rounded-full px-6 py-3.5 text-[15px] font-semibold text-white border border-white/45 transition hover:bg-white/10"
            >
              {t('delivery')}
            </button>
          )}
        </div>
      </div>
    </section>
  )
}
